import { useState, useEffect } from 'react';
import { useWindowSize } from '@Components/Utilities/WindowSize';
import atoms from '@Components/Utilities/SandhillsAtoms';
import { useRecoilValue } from 'recoil';
import { SandhillsSlickSlider, Panel } from '@Components/Shared/SandhillsSlickSlider';
import Link from '@Components/Shared/Controls/Link';
import { sandhillsRequest } from '@Components/Utilities/SandhillsUtility';
import ready from 'document-ready';
import CircularProgress from '@mui/material/CircularProgress';
import translator from '@Components/Utilities/Translator';

const HomePageCarousel = (props) => {
    const windowSize = useWindowSize();
    const isMobileView = windowSize.isMobile;
    const currentUser = useRecoilValue(atoms.currentUser);
    const currentSite = useRecoilValue(atoms.currentWebsite);
    const routes = useRecoilValue(atoms.routes);
    const initialSliderCount = windowSize?.width < 768 ? 2 : windowSize?.width < 1000 ? 3 : 4;
    const [userWatchlist, setUserWatchlist] = useState([]);
    const [selectedCategoryGrid, setSelectedCategoryGrid] = useState(2);
    const [recommendedListingsData] = useState(props?.RecommendedListings || null);
    // props.RecommendedAuctions will be null initially; data will be fetched when the tab is selected
    const [recommendedAuctionsData, setRecommendedAuctionsData] = useState(null);
    const [isLoadingAuctions, setIsLoadingAuctions] = useState(false);

    const getUserWatchlist = () => {
        if (currentUser.IsAuthenticated && !currentSite?.Site?.IsHostedSite && recommendedListingsData?.Cards?.length > 0) {
            sandhillsRequest({
                url: routes.GetWatchList + '/',
                errorHandling: {
                    message: 'Error retrieving watchlist',
                    showDetails: true,
                    useConsole: true,
                    useAlert: false
                }
            }).then((response) => {
                if (response?.length > 0) {
                    setUserWatchlist(response);
                }
            });
        }
    }; 

    const fetchTabData = (tabId) => {
        if (tabId === 1 && !recommendedAuctionsData && !isLoadingAuctions) {
            setIsLoadingAuctions(true);
            sandhillsRequest({
                url: routes.GetRecommendedAuctions + '/',
                method: 'get',
                errorHandling: {
                    message: 'Error retrieving recommended auctions',
                    showDetails: true,
                    useConsole: true,
                    useAlert: false
                }
            }).then((response) => {
                setRecommendedAuctionsData(response);
                setIsLoadingAuctions(false);
            });
        } 
    };

    const handleTabChange = (tabId) => {
        setSelectedCategoryGrid(tabId);
        fetchTabData(tabId);
    };

    const setRecommendedListingsCarouselImageStyles = () => {
        const listings = document.querySelectorAll('.recommendedlistingsslider .listing');
        listings.forEach(listing => {
            const footerHeight = listing.querySelector('.listing-image-footer')?.clientHeight;
            const blurElement = listing.querySelector('.listing-image-blur');
            if (blurElement) {
                blurElement.style.height = window.innerWidth <= 480
                    ? footerHeight > 30 ? '86px' : '101px'
                    : '';
            }
        });
    };

    useEffect(() => {
        // Load initial tab data
        fetchTabData(selectedCategoryGrid);

        if (recommendedListingsData?.Cards?.length && !currentSite?.Site?.IsHostedSite && selectedCategoryGrid === 2) {
            ready(setRecommendedListingsCarouselImageStyles);

            const handleResize = setRecommendedListingsCarouselImageStyles;
            window.addEventListener('resize', handleResize);
            return () => window.removeEventListener('resize', handleResize);
        }
    }, [selectedCategoryGrid, recommendedListingsData]);

    // Prioritize listings tab on all sites
    const getTabsOrder = () => {
        const tabsOrder = [{ id: 2, label: translator.translate('sandhills.RecommendedForYou') }];
        if (props.ShowRecommendedAuctions) {
            tabsOrder.push({ id: 1, label: translator.translate('sandhills.AuctionsForYou') });
        }
        return tabsOrder
    }
    // Get current tab data
    const currentTabData = selectedCategoryGrid === 1 ? recommendedAuctionsData : recommendedListingsData;

    return (
        <section className="home-page-carousel">
            <div className="section-title" style={{ padding: '0px 9px 10px' }}>
                <div className="tab-buttons">
                    {getTabsOrder().map(tab => (
                        <a
                            key={`a-tab-${tab.id}`}
                            role="button"
                            tabIndex="0"
                            onKeyDown={(e) => {
                                if (["Enter", " "].includes(e.key)) {
                                    e.preventDefault();
                                    handleTabChange(tab.id);
                                }
                            }}
                            className={`tab-button ${selectedCategoryGrid === tab.id ? "active" : ""}`}
                            onClick={() => handleTabChange(tab.id)}
                        >
                            <span>{tab.label}</span>
                        </a>
                    ))}
                </div>
                {!isMobileView && currentTabData && (
                    <Link
                        Url={currentTabData?.ViewAllLink}
                        ClassName="view-all-link"
                        text={currentTabData?.ViewAllText}
                        hiddenText={currentTabData?.Title}
                    />
                )}
            </div>
            {[1, 2].map(grid => (
                <div
                    key={`carousel-${grid}`}
                    className={`listing-carousel ${selectedCategoryGrid === grid ? "active" : ""}`}
                    id={grid === 1 ? "auctions-carousel" : "recommended-carousel"}
                    style={{ display: selectedCategoryGrid === grid ? 'block' : 'none' }}
                >
                    {isLoadingAuctions && selectedCategoryGrid === grid ? (
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '40px 0' }}>
                            <CircularProgress sx={{ willChange: 'transform', transform: 'translateZ(0)' }} />
                        </div>
                    ) : (
                        grid === selectedCategoryGrid && currentTabData && (
                            <SandhillsSlickSlider
                                ShowSection
                                {...currentTabData}
                                settings={{
                                    slidesToShow: initialSliderCount,
                                    slidesToScroll: initialSliderCount,
                                    responsive: [
                                        {
                                            breakpoint: 1000,
                                            settings: {
                                                slidesToShow: 3,
                                                slidesToScroll: 3,
                                            }
                                        },
                                        {
                                            breakpoint: 768,
                                            settings: {
                                                slidesToShow: 2,
                                                slidesToScroll: 2,
                                            }
                                        }
                                    ]
                                }}
                                showSectionTitle={false}
                            >
                                {currentTabData?.Cards?.map(card => (
                                    <Panel
                                        key={`slider-${card.Url}`}
                                        PanelType={currentTabData?.ChildComponent}
                                        PanelClass={currentTabData?.PanelClass}
                                        CustomWatchlists={grid === 2 ? currentTabData?.CustomWatchlists : undefined}
                                        getUserWatchlist={grid === 2 ? getUserWatchlist : undefined}
                                        setUserWatchlist={grid === 2 ? setUserWatchlist : undefined}
                                        ShowEvaluatorValues={grid === 2 ? currentTabData?.ShowEvaluatorValues : undefined}
                                        UserWatchlist={grid === 2 ? userWatchlist : undefined}
                                        {...card}
                                    />
                                ))}
                            </SandhillsSlickSlider>
                        )
                    )}
                    {isMobileView && selectedCategoryGrid === grid && currentTabData && (
                        <div className="view-all-button-container">
                            <a className="button ts-button" role='button' tabIndex='0' href={currentTabData?.ViewAllLink}>
                                {currentTabData?.ViewAllText}
                            </a>
                        </div>
                    )}
                </div>
            ))}
        </section>
    );
};

export default HomePageCarousel;